import React, { FC, useRef, useState } from 'react';
import { useLocation, Link } from 'wouter';
import { useMutation } from '@apollo/client';
import * as OrganizationQuery from 'graphql/organization.graphql';
import * as SpaceQuery from 'graphql/space.graphql';
import { currentSpaceSlug } from '~/api/currentSpaceMiddleware';
import useCurrentOrganization from '~/hooks/useCurrentOrganization';
import useSpacesFromCurrentOrg from '~/hooks/useSpacesFromCurrentOrg';
import Avatar from '~/components/Avatar';
import Banner from 'components/Banner';
import DropDown from '~/components/Menu/DropDown';
import DropDownItem from '~/components/Menu/DropDownItem';
import IconButton from '../Button/IconButton';
import ICONS from '~/components/Icons';
import Modal from '~/components/Modal';
import ProtectedComponent, { checkUserRole } from '~/components/ProtectedComponent';
import './style.scss';

type SpaceHeaderProps = {
  renderSpaceSettings?: boolean;
};

const paths = [
  {
    url: '/space-settings/general',
    label: 'Settings',
  },
  {
    url: '/space-settings/members',
    label: 'Settings',
  },
  {
    url: '/pages',
    label: 'Pages',
  },
];

const SpaceHeader: FC<SpaceHeaderProps> = ({ renderSpaceSettings = true }) => {
  const [location, setLocation] = useLocation();
  const spaceSlug = currentSpaceSlug();
  const { currentSpace, spaces } = useSpacesFromCurrentOrg();
  const { currentOrg } = useCurrentOrganization();
  const settingsRef = useRef<HTMLElement>(null);
  const [showModal, setShowModal] = useState(false);

  const [archiveSpace] = useMutation(SpaceQuery.ArchiveSpace, {
    refetchQueries: [{ query: OrganizationQuery.GetOrganization, variables: { currentSpace: currentSpaceSlug() } }],
  });

  const [restoreSpace] = useMutation(SpaceQuery.RestoreSpace, {
    refetchQueries: [{ query: OrganizationQuery.GetOrganization, variables: { currentSpace: currentSpaceSlug() } }],
  });

  const handleArchiveSpace = async () => {
    await archiveSpace();
    setShowModal(false);
  };

  const hasAnotherActiveSpace = !!spaces.find(space => space.slug !== spaceSlug && space.status === 'ACTIVE');
  const spaceAlreadyArchived = currentSpace?.status === 'ARCHIVED';

  const redirectTo = (path: string) => spaceSlug && setLocation(`/${spaceSlug}${path}`);
  const knownPath = paths.find(({ url }) => `/${spaceSlug || ''}${url}` === location);

  const spacePagesLink = `/${currentSpace?.slug}/pages`;

  const canArchiveSpace = checkUserRole('ARCHIVE_SPACE', currentOrg?.currentUserRole, currentSpace?.currentUserRole);

  return (
    <>
      {!!currentSpace && currentSpace.status === 'ARCHIVED' && (
        <Banner className="archived-banner" type="danger">
          <>
            {ICONS['solid_danger']}
            {canArchiveSpace ? (
              <>
                This space is archived,{' '}
                <button className="button-link underline" onClick={() => spaceSlug && void restoreSpace()}>
                  Restore
                </button>
              </>
            ) : (
              <span>This space is archived.</span>
            )}
          </>
        </Banner>
      )}
      {!!currentOrg && !!currentSpace && (
        <div className="space-info-container">
          <div className="space-title">
            <Avatar text={currentSpace.name} size="small" />
            <Link href={spacePagesLink}>
              <h3>{currentSpace.name}</h3>
            </Link>
            {ICONS['arrow_right']}
            {knownPath && <h3>{knownPath.label}</h3>}
          </div>
          {renderSpaceSettings ? (
            <ProtectedComponent
              action="SPACE_SETTINGS"
              currentUserOrganizationRole={currentOrg.currentUserRole}
              currentUserSpaceRole={currentSpace.currentUserRole}>
              <DropDown outsideRef={settingsRef} openedIcon="menu_open" closedIcon="menu_open">
                <DropDownItem icon="settings" onClick={() => redirectTo('/space-settings/general')}>
                  Settings
                </DropDownItem>
                <DropDownItem icon="add_new_member" onClick={() => redirectTo('/space-settings/members')}>
                  Add people
                </DropDownItem>
                {canArchiveSpace && (
                  <DropDownItem
                    icon="archive"
                    onClick={() => setShowModal(true)}
                    disabled={!hasAnotherActiveSpace || spaceAlreadyArchived}>
                    Archive Space
                  </DropDownItem>
                )}
              </DropDown>
            </ProtectedComponent>
          ) : (
            <IconButton icon="close" onClick={() => setLocation(spacePagesLink)} />
          )}
        </div>
      )}
      <Modal
        handleOnCancel={() => {
          setShowModal(false);
        }}
        handleOnConfirm={() => void handleArchiveSpace()}
        confirmlabel="Confirm"
        denyLabel="Cancel"
        header="Archive Space"
        headerIcon="warning"
        visible={showModal}
        className="warning-modal">
        <p>Are you sure you want to archive this space along with all of its pages?</p>
      </Modal>
    </>
  );
};

export default SpaceHeader;
